import { MouseEvent } from "react"
import { createViewContext } from "../../Components/createViewContext"
import { ApiClient, DeviceDebugTraceApi, IDebugNMEAUploadResponse, ILogbookAppNMEAData, ILogbookAppNMEAStatusResult, ILogbookAppStatusResult, INMEAEntry, INMEAResponse, INMEASource, INavigationThresholds, IShipDataEngine, NMEAApi, Routes } from "../../Types"
import { EditGroupData } from "../../Components/EditGroup"
import { SelectOption } from "../../Components/Select"

// A single instrument-source reading (which source last delivered a value and when).
export interface NMEAStatus {
  source: INMEASource;
  receivedAt: string;
}

// Per-check status map keyed by the localized check name (utc, navigation_gps, ...).
export interface NMEAStatusChecks {
  [check: string]: NMEAStatus | undefined;
}

// The state of the debug-capture upload the test-data tab triggers (#1113).
export type NMEADebugCaptureStatus = "idle" | "sending" | "success" | "error";

// Shared data layer for NMEAConfigurationView and its child tab views: the NMEA/
// settings network requests plus the pure entry-status derivations. Extracted so
// the wire contract and the derivation branches can be unit-tested without
// rendering the setup wizard.
export const NMEAConfigurationViewData = {
  loadNmea(api: ApiClient): Promise<INMEAResponse> {
    return new NMEAApi(api).nmea.get({ params: {} }).call();
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  connectNmea(api: ApiClient, values: any) {
    return new NMEAApi(api).nmea.connect({ params: {}, body: values }).call();
  },
  loadStatus(api: ApiClient): Promise<ILogbookAppStatusResult> {
    return new Routes(api).status.get({ params: {} }).call();
  },
  disconnectNmea(api: ApiClient) {
    return new NMEAApi(api).nmea.disconnect({ params: {} }).call();
  },
  setNativePressure(api: ApiClient, enabled: boolean) {
    const nmea = new NMEAApi(api).nmea;
    return (enabled ? nmea.enableBaro({ params: {} }) : nmea.disableBaro({ params: {} })).call();
  },
  // #1113: snapshots the app's always-on rolling buffer of raw received NMEA bytes
  // and uploads it as a device debug trace (raw log plus an inline summary of the
  // sentences/PGNs seen) so support can inspect what the instruments actually sent.
  // Nothing to send in the request -- the app holds the bytes already.
  uploadDebugCapture(api: ApiClient): Promise<IDebugNMEAUploadResponse> {
    return new DeviceDebugTraceApi(api).debugNmea.upload({ params: {} }).call();
  },
  // Button label for the current debug-capture status, falling back to the idle
  // label. The button debounces re-clicks by staying disabled and relabelled while
  // an upload is in flight (the same pattern as AboutView's upload-app-data button).
  debugCaptureLabel(status: NMEADebugCaptureStatus, t: (text: string) => string): string {
    switch (status) {
      case "sending": return t("capture_sending");
      case "success": return t("capture_success");
      case "error": return t("capture_error");
      default: return t("capture");
    }
  },
  // Mirror of the model-side NavigationThresholds.engineIdleActive ordering
  // (engineStop < engineStart < engineIdleRpm): reject configurations the
  // calculation half would silently ignore. Missing values are skipped so a
  // partially-filled form doesn't error. Returns a translation-key suffix
  // (rendered via t("error_" + key)) or undefined when valid.
  validateEngineRpm(stop?: number | null, start?: number | null, idle?: number | null, idleEnabled?: boolean): string | undefined {
    if (stop != null && start != null && stop >= start) {
      return "engine_rpm_order";
    }
    if (idleEnabled && start != null && idle != null && start >= idle) {
      return "engine_idle_rpm";
    }
    return undefined;
  },
  // The status for an instrument entry: the first entry when given an array, and
  // undefined when the entry (or a required attribute) is missing.
  nmeaEntryStatus(entry?: INMEAEntry | INMEAEntry[], attribute?: string): NMEAStatus | undefined {
    if (!entry) {
      return undefined;
    }
    if (Array.isArray(entry)) {
      entry = entry[0];
    }
    if (!entry) {
      return undefined;
    }
    if (attribute && entry[attribute as keyof INMEAEntry] == undefined) {
      return undefined;
    }
    return {
      source: entry.source,
      receivedAt: entry.receivedAt,
    };
  },
  // Like nmeaEntryStatus but restricted to a specific source: picks the matching
  // entry from an array, or drops a single entry whose source differs.
  nmeaEntryStatusForSource(entry?: INMEAEntry | INMEAEntry[], source?: INMEASource, attribute?: string): NMEAStatus | undefined {
    if (!entry) {
      return undefined;
    }
    if (Array.isArray(entry)) {
      entry = entry.find((e) => e.source == source);
    } else if (entry.source != source) {
      return undefined;
    }
    if (!entry) {
      return undefined;
    }
    if (attribute && entry[attribute as keyof INMEAEntry] == undefined) {
      return undefined;
    }
    return {
      source: entry.source,
      receivedAt: entry.receivedAt,
    };
  },
  // #1027 (tracker #976): the full status-check map for the test-data tab. Each check
  // additively falls back to the corresponding `data.n2k.*` reading when the 0183
  // field is absent, so an N2K-only connection lights up the same checks a 0183
  // one would. `native_gps`/`native_air_pressure`/`water_temperature` stay
  // 0183-only: N2K entries never have `source == .native` (no N2K native-sensor
  // path exists).
  buildStatusChecks(data: ILogbookAppNMEAData | undefined, engines: IShipDataEngine[]): NMEAStatusChecks {
    const status = NMEAConfigurationViewData.nmeaEntryStatus;
    const statusForSource = NMEAConfigurationViewData.nmeaEntryStatusForSource;
    return {
      utc: status(data?.navigation, "time") ?? status(data?.n2k?.systemTime, "time"),
      navigation_gps: status(data?.navigation, "lat") ?? status(data?.n2k?.position, "latitude"),
      navigation_course: status(data?.heading) ?? status(data?.n2k?.heading),
      navigation_sog: status(data?.navigation, "sog") ?? status(data?.n2k?.courseSpeed, "sog"),
      navigation_log: status(data?.log) ?? status(data?.n2k?.distanceLog),
      depth: status(data?.depth) ?? status(data?.n2k?.depth),
      wind: status(data?.wind) ?? status(data?.n2k?.wind),
      engine: status(data?.engine) ?? status(data?.n2k?.engine),
      ...(Object.fromEntries(engines.map((e) => {
        const number = e.nmeaNumber != undefined ? e.nmeaNumber : e.number;
        return ["engine_" + e.number, data?.engine?.find((eng) => eng.number == number) ?? data?.n2k?.engine?.find((eng) => eng.instance == number)];
      }))),
      air_pressure: status(data?.weather, "pressure") ?? status(data?.n2k?.pressure, "pressure"),
      air_temperature: status(data?.weather, "temperature") ?? status(data?.n2k?.temperature, "temperature"),
      air_humidity: status(data?.weather, "humidity") ?? status(data?.n2k?.humidity, "actualHumidity"),
      water_temperature: undefined,
      native_gps: statusForSource(data?.navigation, INMEASource.native, "lat"),
      native_air_pressure: statusForSource(data?.weather, INMEASource.native, "pressure"),
    };
  },
  // The combined list of engine ids reporting in, 0183 numbers followed by N2K
  // instances (both addressing schemes, shown together for the test-data tab).
  engineIds(data?: ILogbookAppNMEAData): number[] {
    const nmeaIds = data?.engine?.map((e) => e.number) ?? [];
    const n2kIds = (data?.n2k?.engine ?? []).map((e) => e.instance).filter((n): n is number => n !== undefined);
    return [...nmeaIds, ...n2kIds];
  },
};

// Shared runtime the four tab views read from the root NMEAConfigurationView. Only
// parent-owned state, callbacks and derived data flow through here; children
// re-acquire app/ship/t themselves via hooks.
export interface NMEAConfigurationContextData {
  // connection tab
  connecting: boolean;
  connectionType: string;
  connectionTypes: SelectOption[];
  selectConnectionType: (type: string) => void;
  initConnectionTypeOpen?: boolean;
  host?: string;
  port?: number;
  device?: string;
  devices: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  connect: (data: FormData, values: any) => Promise<void>;
  resetConnection: (e: MouseEvent<HTMLButtonElement>) => Promise<void>;
  // native tab
  nativePressureChecked: boolean;
  setNativePressure: (enabled: boolean) => Promise<void>;
  // test_data tab
  statusChecks: NMEAStatusChecks;
  status?: ILogbookAppNMEAStatusResult;
  // conditions tab
  interval?: number;
  intervalOptions: { title: string; value: number }[];
  selectIntervalOptions: (type: number) => void;
  courseChecked: boolean;
  setCourseChecked: (checked: boolean) => void;
  engineIdleChecked: boolean;
  setEngineIdleChecked: (checked: boolean) => void;
  startStopAnchorChecked: boolean;
  setStartStopAnchorChecked: (checked: boolean) => void;
  thresholds?: INavigationThresholds;
  updateThresholds: (data: FormData, values: EditGroupData) => Promise<void>;
  // shared
  edited: boolean;
  onEdited: () => void;
  error?: string;
  completedCallback: () => void;
}

const nmeaConfigurationContext = createViewContext<NMEAConfigurationContextData>();
export const NMEAConfigurationContext = nmeaConfigurationContext.Context;
export const GetNMEAConfigurationContext = nmeaConfigurationContext.useGet;
